import {testIdProps} from '@github-ui/test-id-props'
import {clsx} from 'clsx'
import {memo, useMemo, useRef} from 'react'

import {getGroupTestId} from '../../helpers/table-group-utilities'
import type {Row} from '../../hooks/tanstack-table/helpers/types'
import {useAggregationSettings} from '../../hooks/use-aggregation-settings'
import {useDroppableGroup} from '../../hooks/use-droppable-group'
import {useTableRowHeight} from '../../hooks/use-table-row-height'
import useIsVisible from '../board/hooks/use-is-visible'
import {GroupHeader} from '../common/group/group-header'
import {useScrollGroupIntoViewOnToggle} from '../common/group/hooks/use-scroll-group-into-view-on-toggle'
import {GroupHeaderLabel} from '../common/group/label/group-header-label'
import {GROUP_HEADER_HEIGHT} from '../common/group/styled-group-header'
import {GROUP_SEPARATOR_HEIGHT} from './constants'
import {
  TABLE_GROUP_FOOTER_HEIGHT,
  TABLE_GROUP_FOOTER_MARGIN_TOP,
  TableGroupContents,
} from './group/table-group-contents'
import {isCellFocus, isGroupOmnibarFocus, type TableFocus, useTableNavigation} from './navigation'
import {useIsDraggingRows} from './row-reordering/hooks/use-is-dragging-rows'
import {ReorderableRowsContext} from './row-reordering/reorderable-rows'
import type {TableDataType} from './table-data-type'
import styles from './table-group.module.css'
import type {TableGroupProps} from './table-group-props'
import {TableGroupSeparator} from './table-group-separator'

const BORDER_WIDTH = 1
const TABLE_GROUP_FIXED_HEIGHT = GROUP_HEADER_HEIGHT + BORDER_WIDTH + GROUP_SEPARATOR_HEIGHT
const ADD_ITEM_ROW_HEIGHT = TABLE_GROUP_FOOTER_HEIGHT + TABLE_GROUP_FOOTER_MARGIN_TOP + BORDER_WIDTH

function isFocused(focus: TableFocus, rows: Array<Row<TableDataType>>, groupId: string) {
  if (isCellFocus(focus)) {
    return rows.some(row => row.id === focus.details.y)
  }

  return isGroupOmnibarFocus(focus, groupId)
}

/**
 * This is used to display a grouped set of rows as the output of a group-by operation.
 */
export const TableGroup = memo(function TableGroupComponent({
  groupId,
  metadata,
  rows,
  itemData,
  scrollRef,
  isCollapsed,
  shouldDisableFooter,
  footerPlaceholder,
  isEditable,
  totalCount,
  headerRef,
}: TableGroupProps) {
  const ref = useRef<HTMLDivElement | null>(null)
  const title = getGroupTestId(metadata.sourceObject)
  const {rowHeightPx} = useTableRowHeight()

  // Estimate the rendered size of the group by summing the variable height of
  // rows with the fixed height of headers, footers and spacing.
  const variableHeight = isCollapsed ? 0 : rows.length * rowHeightPx + ADD_ITEM_ROW_HEIGHT
  const defaultHeight = variableHeight + TABLE_GROUP_FIXED_HEIGHT

  const {isVisible, size} = useIsVisible({ref, defaultHeight})

  // When this group of rows is visible, we set the height to 'unset' so that we inherit the true
  // height of the group after all of its contents have been painted. Otherwise, we render a fixed
  // height as a placeholder.
  const height = isVisible ? 'unset' : `${size}px`
  const style = useMemo(() => ({height}), [height])

  const {
    state: {focus},
  } = useTableNavigation()

  const {hideItemsCount, getAggregatesForGroupId} = useAggregationSettings()

  const aggregates = useMemo(() => getAggregatesForGroupId(metadata.value), [getAggregatesForGroupId, metadata.value])

  // If we don't render the rows when dragging, the `useReorderableRow` hook will get unmounted and dnd-kit will not
  // provide any drop data
  const isDragging = useIsDraggingRows(rows)

  // It's important that we render the element if it's focused, even if it is
  // eligible for virtualization, because if we don't, programmatic `focus()`
  // will cause Safari to auto-scroll to the focused element. See
  // https://github.com/github/memex/issues/2772 for more context.
  const shouldRender = isVisible || isDragging || (focus && isFocused(focus, rows, groupId))

  useScrollGroupIntoViewOnToggle({isCollapsed, groupRef: ref, scrollRef, headerRef})

  const {setNodeRef, isOver} = useDroppableGroup({
    groupId,
    groupedValue: metadata.value,
    isCollapsed,
    isEmpty: rows.length === 0,
  })

  return (
    <div style={style} ref={ref} {...testIdProps(`table-group-${title}`)} role="rowgroup">
      {shouldRender ? (
        <>
          <div ref={setNodeRef} className={clsx(styles.Box, isOver && styles.isOver)}>
            <GroupHeader
              role="row"
              isCollapsed={isCollapsed}
              metadata={metadata}
              itemsInGroup={rows.map(row => row.original)}
              viewHeaderRef={headerRef}
              sticky
            >
              <GroupHeaderLabel
                sourceObject={metadata.sourceObject}
                rowCount={totalCount}
                aggregates={aggregates}
                hideItemsCount={hideItemsCount}
              />
            </GroupHeader>
            <ReorderableRowsContext siblingRows={rows} groupedValue={metadata.value}>
              <TableGroupContents
                groupId={groupId}
                isCollapsed={isCollapsed}
                itemData={itemData}
                metadata={metadata}
                rows={rows}
                scrollRef={scrollRef}
                shouldDisableFooter={shouldDisableFooter}
                footerPlaceholder={footerPlaceholder}
                isEditable={isEditable}
              />
            </ReorderableRowsContext>
          </div>
          <TableGroupSeparator />
        </>
      ) : null}
    </div>
  )
})
